import * as Sentry from '@sentry/react';
import { backendApi } from '_config';
import {
    tooManyRequestsStatusCodes,
    unauthorisedStatusCodes,
} from '_constants/statusCodes';
import { isUrlProtected } from '_router/Router';
import { clearCookiesAndSetLoggedOut } from '_services/LocalStorage/LoggedIn';
import { flashMessageSlice } from '_services/Redux/FlashMessage/FlashMessageSlice';
import { runApiDiagnostics } from '_utils/apiConnectionTest';
import { xsrfTokenRequestInterceptor } from '_utils/xsrfInterceptor';
import axios, {
    AxiosError,
    type AxiosRequestConfig,
    type AxiosResponse,
} from 'axios';
import Csrf from './Csrf';
import {
    callWithRequestLimiter,
    type AxiosRequestShape as UtilAxiosRequestShape,
} from './_utils/callWithRequestLimiter';

export const AxiosApi = axios.create({
    baseURL: backendApi,
    headers: {
        'Content-type': 'application/json',
    },
    withCredentials: true,
});
AxiosApi.interceptors.request.use(xsrfTokenRequestInterceptor);

export const handleError = (
    responseStatus: string | number,
    error: unknown | AxiosError,
): unknown => {
    /**
     * When application is in maintenance mode
     */
    if ([503, '503'].includes(responseStatus)) {
        window.location.href = '/maintenance';
    } else if (![404, '404', 0, '0'].includes(responseStatus)) {
        // Add detailed logging for network errors
        if (error instanceof AxiosError && error.code === 'ERR_NETWORK') {
            // Run API diagnostics to help identify the issue
            runApiDiagnostics();
            Sentry.captureException(error);
        }
    }

    const { sayError } = flashMessageSlice.actions;

    if (unauthorisedStatusCodes.includes(responseStatus)) {
        Sentry.addBreadcrumb({
            category: 'auth',
            level: 'info',
            message: 'Unauthorised response: redirecting to login',
            data: {
                status: responseStatus,
                path: window.location.pathname,
            },
        });
        clearCookiesAndSetLoggedOut();

        const path = window.location.pathname;
        if (isUrlProtected(path)) {
            // Saving protected path in lastLocation,
            // so that user is automatically redirected to where they were after loging in
            window.location.href = `/login?lastLocation=${path}`;
        } else if (!path.includes('login')) {
            window.location.href = `/login`;
        }

        if (
            axios.isAxiosError(error) &&
            error?.response?.data?.message !== 'CSRF token mismatch.'
        ) {
            // Use dynamic import to avoid circular dependency
            import('_services/Redux/store').then(({ dispatch }) => {
                dispatch(
                    sayError(
                        'Session expired. Please log in again to continue',
                    ),
                );
            });
        }
    } else if (tooManyRequestsStatusCodes.includes(responseStatus)) {
        Sentry.addBreadcrumb({
            category: 'rate_limit',
            level: 'warning',
            message: 'Too many requests response encountered',
            data: {
                status: responseStatus,
            },
        });
        // Use dynamic import to avoid circular dependency
        import('_services/Redux/store').then(({ dispatch }) => {
            dispatch(
                sayError(
                    'There has been too many requests. Please wait a minute before continuing',
                ),
            );
        });
    } else if (error instanceof AxiosError && error.code === 'ERR_NETWORK') {
        Sentry.addBreadcrumb({
            category: 'network',
            level: 'error',
            message: 'Network error during API call',
            data: {
                baseURL: (error.config?.baseURL ?? null) as unknown as string,
                url: error.config?.url,
                method: error.config?.method,
            },
        });
        // Use dynamic import to avoid circular dependency
        import('_services/Redux/store').then(({ dispatch }) => {
            dispatch(sayError('Error, Please check your internet connection'));
        });
    }

    return error;
};

AxiosApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        const responseStatus = error.response?.status;
        const originalConfig = error.config ?? {};

        // Special handling for CSRF 419: refresh cookie and retry once
        if (
            (responseStatus === 419 || responseStatus === '419') &&
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            !(originalConfig as any)._csrfRetried
        ) {
            try {
                Sentry.addBreadcrumb({
                    category: 'csrf',
                    level: 'info',
                    message:
                        'CSRF 419 received: refreshing cookie and retrying',
                    data: {
                        url: originalConfig.url,
                        method: originalConfig.method,
                        baseURL: originalConfig.baseURL,
                    },
                });
                await Csrf.createCsrfCookie();
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (originalConfig as any)._csrfRetried = true;
                return AxiosApi(originalConfig);
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (e) {
                Sentry.addBreadcrumb({
                    category: 'csrf',
                    level: 'error',
                    message: 'CSRF retry failed after refresh',
                    data: {
                        url: originalConfig.url,
                        method: originalConfig.method,
                    },
                });
                // fall through to standard handling
            }
        }

        // handling error here
        return Promise.reject(handleError(responseStatus, error));
    },
);

// Re-export type for backward compatibility
export type AxiosRequestShape = UtilAxiosRequestShape;

// Re-export for backward compatibility
export { callWithRequestLimiter };

const Api = {
    post: (
        url: string,
        data?: AxiosRequestConfig['data'],
    ): Promise<AxiosResponse> =>
        callWithRequestLimiter({ method: 'POST', url, body: data }),
    get: (
        url: string,
        data?: {
            params: AxiosRequestConfig['params'];
        },
    ): Promise<AxiosResponse> =>
        callWithRequestLimiter({ method: 'GET', url, ...data }),
    put: (
        url: string,
        data?: AxiosRequestConfig['data'],
    ): Promise<AxiosResponse> =>
        callWithRequestLimiter({ method: 'PUT', url, body: data }),
    patch: (
        url: string,
        data?: AxiosRequestConfig['data'],
    ): Promise<AxiosResponse> =>
        callWithRequestLimiter({ method: 'PATCH', url, body: data }),
    delete: (url: string): Promise<AxiosResponse> =>
        callWithRequestLimiter({ url, method: 'DELETE' }),
};

export default Api;
